import { useEffect, useState } from "react";
import { Modal, Table, Select, Space, Tag, Typography } from "antd";
import moment from "moment";
import useLoteNotificacionAPI, { LoteNotificacionDetalle, PREFIJO_DESCARTADA, esDescartada } from "../hooks/useLoteNotificacionAPI";
import EstadoLoteNotificacion from "./EstadoLoteNotificacion";

const { Text } = Typography;

export interface LoteNotificacionDetalleModalProps {
    loteNotificacionId: number | null
    open: boolean
    onClose: () => void
}

type FiltroEstado = "TODAS" | "PENDIENTES" | "ENVIADAS" | "FALLIDAS" | "DESCARTADAS";

const PAGE_SIZE = 20;

const estadoTag = (enviado: boolean | null, record: LoteNotificacionDetalle) => {
    if (enviado == null) return <Tag color="blue">Pendiente</Tag>;
    if (enviado) return <Tag color="green">Enviado</Tag>;
    return esDescartada(record.Error) ? <Tag color="orange">Descartada</Tag> : <Tag color="red">Fallido</Tag>;
};

// El motivo de una descartada se muestra sin el prefijo técnico.
const errorTexto = (error?: string) => {
    if (!error) return "-";
    return esDescartada(error) ? error.substring(PREFIJO_DESCARTADA.length) : error;
};

export default (props: LoteNotificacionDetalleModalProps) => {
    const api = useLoteNotificacionAPI();
    const [items, setItems] = useState<LoteNotificacionDetalle[]>([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [filtroEstado, setFiltroEstado] = useState<FiltroEstado>("TODAS");
    const [loading, setLoading] = useState(false);

    const filtroToClause = (): string[] => {
        switch (filtroEstado) {
            case "PENDIENTES": return ["Enviado eq null"];
            case "ENVIADAS": return ["Enviado eq true"];
            case "FALLIDAS": return [`Enviado eq false and (Error eq null or not startswith(Error,'${PREFIJO_DESCARTADA}'))`];
            case "DESCARTADAS": return [`Enviado eq false and startswith(Error,'${PREFIJO_DESCARTADA}')`];
            default: return [];
        }
    };

    const load = async () => {
        if (!props.loteNotificacionId) return;
        setLoading(true);
        const result = await api.listarDetalleLote(props.loteNotificacionId, {
            $count: true,
            $top: PAGE_SIZE,
            $skip: (page - 1) * PAGE_SIZE,
            $filter: filtroToClause(),
        });
        setItems(result.data?.value ?? []);
        setTotal(result.data?.["@odata.count"] ?? 0);
        setLoading(false);
    };

    useEffect(() => {
        if (props.open) {
            load();
        }
    }, [props.open, props.loteNotificacionId, page, filtroEstado]);

    useEffect(() => {
        if (props.open) {
            setPage(1);
        }
    }, [props.open, props.loteNotificacionId]);

    return (
        <Modal
            title="Detalle del lote de notificación"
            open={props.open}
            onCancel={props.onClose}
            footer={null}
            width={800}
        >
            {props.loteNotificacionId && (
                <div style={{ marginBottom: 16 }}>
                    <EstadoLoteNotificacion loteNotificacionId={props.loteNotificacionId} />
                </div>
            )}
            <Space style={{ marginBottom: 16 }}>
                <Text>Mostrar:</Text>
                <Select value={filtroEstado} onChange={setFiltroEstado} style={{ width: 160 }}>
                    <Select.Option value="TODAS">Todas</Select.Option>
                    <Select.Option value="PENDIENTES">Pendientes</Select.Option>
                    <Select.Option value="ENVIADAS">Enviadas</Select.Option>
                    <Select.Option value="FALLIDAS">Fallidas</Select.Option>
                    <Select.Option value="DESCARTADAS">Descartadas</Select.Option>
                </Select>
            </Space>
            <Table
                rowKey="Id"
                size="small"
                loading={loading}
                dataSource={items}
                pagination={{
                    current: page,
                    pageSize: PAGE_SIZE,
                    total,
                    onChange: setPage,
                }}
            >
                <Table.Column title="Nro. trámite" dataIndex="NroTramite" />
                <Table.Column title="Estado" dataIndex="Enviado" render={estadoTag} />
                <Table.Column
                    title="Fecha de envío"
                    dataIndex="FechaEnvio"
                    render={(fecha?: string) => (fecha ? moment(fecha).format("DD/MM/YYYY HH:mm:ss") : "-")}
                />
                <Table.Column title="Error / motivo" dataIndex="Error" render={errorTexto} />
            </Table>
        </Modal>
    );
};
